import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Footer from 'src/components/Footer'
import NewHeader from 'src/New Codes/components/NewHeader'
import { node_url, url } from 'src/helpers'
import { renderBody } from './textUtils'
import './BlogPost.css'

function readingMinutes(body) {
    const words = (body || '').replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length
    return Math.max(1, Math.round(words / 200))
}

const BlogPost = () => {
    const { slug } = useParams()
    const [post, setPost] = useState(null)
    const [structuredData, setStructuredData] = useState(null)
    const [related, setRelated] = useState([])
    const [loading, setLoading] = useState(true)
    const [copied, setCopied] = useState(false)

    async function fetchData() {
        setLoading(true)
        try {
            let alreadyCounted = false
            try { alreadyCounted = !!sessionStorage.getItem('blog_viewed_' + slug) } catch (e) { }
            const response = await fetch(url + 'blog/' + slug + (alreadyCounted ? '?count=0' : ''))
            const data = await response.json()
            if (data?.status === 200) {
                setPost(data?.data)
                try { sessionStorage.setItem('blog_viewed_' + slug, '1') } catch (e) { }
                setStructuredData(data?.structured_data || null)
            } else {
                setPost(null)
                toast.error(data?.message || 'Post not found')
            }
        } catch (err) {
            toast.error('Failed to load blog post')
        } finally {
            setLoading(false)
        }
    }

    async function fetchRelated() {
        try {
            const response = await fetch(url + 'blog')
            const data = await response.json()
            if (data?.status === 200) {
                setRelated((data?.data || []).filter((item) => item.slug !== slug).slice(0, 3))
            }
        } catch (err) {
            setRelated([])
        }
    }

    useEffect(() => {
        if (!slug) return
        window.scrollTo(0, 0)
        fetchData()
        fetchRelated()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug])

    useEffect(() => {
        if (post?.meta_title || post?.title) document.title = (post.meta_title || post.title) + ' | Beba Mzigo'
    }, [post])

    const bodyHtml = useMemo(() => renderBody(post?.body_html), [post])
    const pageUrl = window.location.href

    function copyLink() {
        const done = () => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(pageUrl).then(done).catch(() => toast.error('Could not copy the link'))
        } else {
            toast.error('Could not copy the link')
        }
    }

    const shareText = encodeURIComponent(post?.title || '')
    const shareUrl = encodeURIComponent(pageUrl)

    return (
        <>
            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}
            <NewHeader />
            <div className="blog-post-page">
                {loading ? (
                    <div className="bp-state">Loading...</div>
                ) : post ? (
                    <>
                        <header className="bp-hero">
                            <div className="bp-wrap">
                                <Link to="/blog" className="bp-back"><i className="fa fa-arrow-left"></i> All blogs</Link>
                                {post?.category && <span className="bp-category">{post.category}</span>}
                                <h1 className="bp-title">{post?.title}</h1>
                                {post?.meta_description && <p className="bp-lead">{post.meta_description}</p>}
                                <div className="bp-meta">
                                    <span><i className="fa fa-calendar-o"></i> {new Date(post?.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                    <span><i className="fa fa-clock-o"></i> {readingMinutes(post?.body_html)} min read</span>
                                    <span><i className="fa fa-eye"></i> {(post?.views || 0).toLocaleString()} {post?.views === 1 ? 'view' : 'views'}</span>
                                </div>
                            </div>
                        </header>

                        <div className="bp-wrap">
                            {post?.cover_image && (
                                <img src={`${node_url}${post.cover_image}`} className="bp-cover" alt={post.title} />
                            )}

                            <article className="bp-body" dangerouslySetInnerHTML={{ __html: bodyHtml }}></article>

                            {post?.tags?.length > 0 && (
                                <div className="bp-tags">
                                    {post.tags.map((tag, index) => (
                                        <span key={index} className="bp-tag">#{tag}</span>
                                    ))}
                                </div>
                            )}

                            <div className="bp-share">
                                <span className="bp-share-label">Share this post</span>
                                <a className="bp-share-btn" target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" href={`https://wa.me/?text=${shareText}%20${shareUrl}`}><i className="fa-brands fa-whatsapp"></i></a>
                                <a className="bp-share-btn" target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}><i className="fa-brands fa-facebook-f"></i></a>
                                <a className="bp-share-btn" target="_blank" rel="noopener noreferrer" aria-label="Share on X" href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}><i className="fa-brands fa-x-twitter"></i></a>
                                <button type="button" className="bp-share-btn" aria-label="Copy link" onClick={copyLink}>
                                    <i className={`fa ${copied ? 'fa-check' : 'fa-link'}`}></i>
                                </button>
                            </div>
                        </div>

                        {related.length > 0 && (
                            <section className="bp-related">
                                <div className="bp-wrap bp-wrap-wide">
                                    <h2>More blogs</h2>
                                    <div className="bp-related-grid">
                                        {related.map((item) => (
                                            <Link to={`/blog/${item.slug}`} className="bp-related-card" key={item._id}>
                                                {item.cover_image ? (
                                                    <img src={`${node_url}${item.cover_image}`} alt={item.title} />
                                                ) : (
                                                    <div className="bp-related-ph"><i className="fa fa-newspaper-o"></i></div>
                                                )}
                                                <div className="bp-related-body">
                                                    {item.category && <span className="bp-category small">{item.category}</span>}
                                                    <h3>{item.title}</h3>
                                                    <span className="bp-related-date">{new Date(item.created_at).toLocaleDateString()}</span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </section>
                        )}
                    </>
                ) : (
                    <div className="bp-state">
                        <p>This blog post could not be found.</p>
                        <Link to="/blog" className="bp-btn">Back to all blogs</Link>
                    </div>
                )}
            </div>
            <Footer />
        </>
    )
}

export default BlogPost
